<!--
 * @Description: 巡检计划新增/编辑侧边弹窗
-->
<template>
  <ykc-drawer
    :title="drawerTitle"
    size="800px"
    :show.sync="drawerVisible"
    :on-close="onDrawerClose"
    :before-cancel="onDrawerCancel">
    <template #footer>
      <div class="drawer-button-wrap">
        <template v-for="(btn, i) in drawerButtons">
          <ykc-button :type="btn.type" v-if="btn.enabled()" :key="i" @click="btn.handler">
            {{ btn.label }}
          </ykc-button>
        </template>
      </div>
    </template>
    <div class="drawer-main-body" v-if="drawerVisible">
      <!-- 前置配置 -->
      <ykc-form
        ref="preForm"
        :model="preForm"
        :rules="preRules"
        label-width="140px"
        v-show="!showStationConfig">
        <ykc-form-item label="巡检城市" prop="cityId">
          <ykc-dropdown
            placeholder="请选择巡检城市"
            :data="cityData"
            filterable
            v-model="preForm.cityId"
            @change="onCityChange"></ykc-dropdown>
        </ykc-form-item>
        <ykc-form-item label="巡检日期" prop="inspectionDate">
          <el-date-picker
            v-model="preForm.inspectionDate"
            type="date"
            placeholder="请选择巡检日期"
            value-format="yyyy-MM-dd"
            :picker-options="pickerOptions"></el-date-picker>
        </ykc-form-item>
        <ykc-form-item label="计划巡检电站数量" prop="stationCount">
          <ykc-input
            v-model="preForm.stationCount"
            type="number"
            placeholder="请输入正整数"></ykc-input>
        </ykc-form-item>
        <ykc-form-item label="巡检行程起点" prop="startAddress">
          <div class="flex">
            <ykc-input
              v-model="preForm.startAddress"
              placeholder="请输入位置检索"
              @blur="onStartAddressBlur"
              style="flex: 1; margin-right: 10px"></ykc-input>
            <ykc-button @click="clickStartAddress">地图定位</ykc-button>
          </div>
        </ykc-form-item>
        <ykc-form-item>
          <div class="flex">
            <station-map
              ref="startMap"
              class="ykc-map"
              style="flex: 1"
              @mapClick="onStartMapClick"></station-map>
          </div>
        </ykc-form-item>
      </ykc-form>

      <!-- 巡检电站配置（生成AI后展示） -->
      <div v-show="showStationConfig">
        <ykc-form ref="stationForm" :model="stationForm" :rules="stationRules" label-width="140px">
          <ykc-form-item label="巡检城市">
            <span>{{ stationForm.cityName }}</span>
          </ykc-form-item>
          <ykc-form-item label="巡检日期">
            <span>{{ stationForm.inspectionDate }}</span>
          </ykc-form-item>
          <ykc-form-item label="计划巡检电站数量">
            <span>{{ stationForm.stationCount }}</span>
          </ykc-form-item>
          <ykc-form-item label="巡检行程起点">
            <span>{{ stationForm.startAddress }}</span>
          </ykc-form-item>

          <!-- 智能建议巡检电站 -->
          <div class="station-list-section">
            <div class="section-title">
              智能建议巡检电站（总里程{{ stationForm.aiTotalMileage || 0 }}km）
            </div>
            <div
              v-for="(station, index) in stationForm.aiStationList"
              :key="'ai-' + index"
              class="station-item">
              <span class="station-sort">第{{ index + 1 }}站</span>
              <span class="station-name">{{ station.stationName }}</span>
              <span class="station-mileage">
                第{{ index + 1 }}程{{ station.segmentMileage || 0 }}km
              </span>
            </div>
          </div>

          <!-- 计划巡检电站 -->
          <div class="station-list-section">
            <div class="section-title">
              计划巡检电站（总里程{{ formatMileage(stationForm.totalMileage) }}km）
            </div>
            <div
              v-for="(station, index) in stationForm.planStationList"
              :key="'plan-' + index"
              class="station-item">
              <span class="station-sort">第{{ index + 1 }}站</span>
              <ykc-dropdown
                v-model="station.stationId"
                :data="stationOptions"
                filterable
                placeholder="请选择电站"
                style="width: 200px"
                @change="val => onPlanStationChange(index, val)"></ykc-dropdown>
              <span class="station-mileage">
                第{{ index + 1 }}程{{ formatMileage(station.segmentMileage) }}km
              </span>
              <ykc-button type="text" class="clear-btn" @click="clearStation(index)">
                清空
              </ykc-button>
            </div>
          </div>

          <ykc-form-item label="巡检电站手动调整原因" prop="adjustReason">
            <el-input
              v-model="stationForm.adjustReason"
              type="textarea"
              :rows="3"
              maxlength="500"
              placeholder="智能建议与计划巡检电站不一致时必填"
              :disabled="!needAdjustReason"></el-input>
          </ykc-form-item>
        </ykc-form>
      </div>
    </div>
  </ykc-drawer>
</template>

<script>
  import YkcDialog from '@/components/ykc-ui/dialog';
  import StationMap from '@/views/plantAssets/powerStationManagement/directConnectionStation/StationMap';
  import { inspectionPlanApi } from '@/service/apis';

  export default {
    name: 'InspectionPlanAddOrEdit',
    components: { StationMap },
    props: {
      visible: { type: Boolean, default: false },
      editId: { type: [Number, String], default: null },
    },
    data() {
      return {
        generating: false,
        saving: false,
        showStationConfig: false,
        preForm: {
          cityId: '',
          cityName: '',
          inspectionDate: '',
          stationCount: null,
          startAddress: '',
          startLongitude: null,
          startLatitude: null,
        },
        stationForm: {
          id: null,
          cityId: '',
          cityName: '',
          inspectionDate: '',
          stationCount: null,
          startAddress: '',
          startLongitude: null,
          startLatitude: null,
          totalMileage: 0,
          aiTotalMileage: 0,
          adjustReason: '',
          aiStationList: [],
          planStationList: [],
        },
        cityData: [],
        stationOptions: [],
        pickerOptions: {
          disabledDate(time) {
            return time.getTime() < Date.now() - 24 * 60 * 60 * 1000;
          },
        },
        preRules: {
          cityId: [{ required: true, message: '请选择巡检城市', trigger: 'change' }],
          inspectionDate: [{ required: true, message: '请选择巡检日期', trigger: 'change' }],
          stationCount: [
            { required: true, message: '请输入计划巡检电站数量', trigger: 'blur' },
            {
              validator: (rule, value, callback) => {
                if (value && (!/^\d+$/.test(value) || parseInt(value, 10) <= 0)) {
                  callback(new Error('请输入正整数'));
                } else {
                  callback();
                }
              },
              trigger: 'blur',
            },
          ],
          startAddress: [{ required: true, message: '请输入巡检行程起点', trigger: 'blur' }],
        },
        stationRules: {
          adjustReason: [
            {
              validator: (rule, value, callback) => {
                if (this.needAdjustReason && !value) {
                  callback(new Error('巡检电站手动调整原因必填'));
                } else {
                  callback();
                }
              },
              trigger: 'blur',
            },
          ],
        },
      };
    },
    computed: {
      drawerVisible: {
        get() {
          return this.visible;
        },
        set(val) {
          this.$emit('update:visible', val);
        },
      },
      drawerTitle() {
        return this.editId ? '编辑巡检计划' : '新建巡检计划';
      },
      needAdjustReason() {
        const aiList = this.stationForm.aiStationList;
        const planList = this.stationForm.planStationList;
        if (!aiList || !planList || aiList.length !== planList.length) {
          return true;
        }
        for (let i = 0; i < aiList.length; i++) {
          if (aiList[i].stationId !== planList[i].stationId) {
            return true;
          }
        }
        return false;
      },
      drawerButtons() {
        return [
          {
            label: '取消',
            type: 'plain',
            enabled: () => true,
            handler: () => {
              this.handleCancel();
            },
          },
          {
            label: this.showStationConfig ? '保存' : '生成巡检计划',
            enabled: () => true,
            handler: () => {
              if (this.showStationConfig) {
                this.handleSave();
              } else {
                this.handleGenerate();
              }
            },
          },
        ];
      },
    },
    mounted() {
      console.log('inspectionConfig mounted');
      this.resetForm();
      this.loadCityData();
      if (this.editId) {
        this.loadEditData();
      }
    },
    methods: {
      resetForm() {
        this.showStationConfig = false;
        this.preForm = {
          cityId: '',
          cityName: '',
          inspectionDate: '',
          stationCount: null,
          startAddress: '',
          startLongitude: null,
          startLatitude: null,
        };
        this.stationForm = {
          id: null,
          cityId: '',
          cityName: '',
          inspectionDate: '',
          stationCount: null,
          startAddress: '',
          startLongitude: null,
          startLatitude: null,
          totalMileage: 0,
          aiTotalMileage: 0,
          adjustReason: '',
          aiStationList: [],
          planStationList: [],
        };
      },
      loadCityData() {
        inspectionPlanApi.getMaintenanceCityList().then(res => {
          this.cityData = (res || []).map(item => ({
            id: item.cityId || item.id,
            name: item.cityName || item.name,
          }));
        });
      },
      loadEditData() {
        inspectionPlanApi
          .getDetail(this.editId)
          .then(res => {
            if (res) {
              this.preForm.cityId = res.cityId;
              this.preForm.cityName = res.cityName;
              this.preForm.inspectionDate = res.inspectionDate;
              this.preForm.stationCount = res.stationCount;
              this.preForm.startAddress = res.startAddress;
              this.preForm.startLongitude = res.startLongitude;
              this.preForm.startLatitude = res.startLatitude;
              this.stationForm.id = res.id;
              this.stationForm.cityId = res.cityId;
              this.stationForm.cityName = res.cityName;
              this.stationForm.inspectionDate = res.inspectionDate;
              this.stationForm.stationCount = res.stationCount;
              this.stationForm.startAddress = res.startAddress;
              this.stationForm.startLongitude = res.startLongitude;
              this.stationForm.startLatitude = res.startLatitude;
              this.stationForm.totalMileage = res.totalMileage;
              this.stationForm.aiTotalMileage = res.aiTotalMileage;
              this.stationForm.adjustReason = res.adjustReason;
              this.stationForm.aiStationList = res.aiStationList || [];
              this.stationForm.planStationList = res.planStationList || [];
              this.showStationConfig = true;
              // 加载该城市下的电站列表，用于计划巡检电站下拉选择
              this.loadStationOptions(res.cityId);
            }
          })
          .catch(err => {
            console.error('加载编辑数据失败:', err);
          });
      },
      onCityChange(val) {
        const city = this.cityData.find(c => c.id === val);
        this.preForm.cityName = city ? city.name : '';
        // 加载该城市下的电站列表
        this.loadStationOptions(val);
      },
      loadStationOptions(cityId) {
        if (!cityId) {
          this.stationOptions = [];
          return;
        }
        inspectionPlanApi.getStationsByCity(cityId).then(res => {
          this.stationOptions = (res || []).map(item => ({
            id: item.id,
            name: item.name,
            longitude: item.longitude,
            latitude: item.latitude,
            platform: item.platform,
          }));
          // 编辑回显时，用电站列表补全 planStationList 中缺失的经纬度
          if (this.editId && this.stationForm.planStationList.length > 0) {
            this.stationForm.planStationList.forEach((station, index) => {
              if (station.stationId && (!station.longitude || !station.latitude)) {
                const opt = this.stationOptions.find(s => s.id === station.stationId);
                if (opt) {
                  this.$set(this.stationForm.planStationList, index, {
                    ...station,
                    longitude: opt.longitude,
                    latitude: opt.latitude,
                    platform: opt.platform,
                  });
                }
              }
            });
            // 补全经纬度后重新计算里程
            this.recalcPlanMileage();
          }
        });
      },
      onPlanStationChange(index, val) {
        // 校验是否已选择过该电站
        const duplicate = this.stationForm.planStationList.some(
          (s, i) => i !== index && s.stationId === val
        );
        if (duplicate) {
          this.$message.warning('该电站已选择，请勿重复选择');
          // 还原为空
          this.$set(this.stationForm.planStationList, index, {
            ...this.stationForm.planStationList[index],
            stationId: null,
            stationName: '',
          });
          return;
        }
        const station = this.stationOptions.find(s => s.id === val);
        if (station) {
          this.$set(this.stationForm.planStationList, index, {
            ...this.stationForm.planStationList[index],
            stationId: station.id,
            stationName: station.name,
            longitude: station.longitude,
            latitude: station.latitude,
            platform: station.platform,
          });
          // 重新计算里程
          this.recalcPlanMileage();
        }
      },
      /**
       * 里程格式化：保留2位小数
       */
      formatMileage(val) {
        return (Number(val) || 0).toFixed(2);
      },
      /**
       * 重新计算计划巡检电站的各程里程和总里程
       */
      recalcPlanMileage() {
        const list = this.stationForm.planStationList;
        let prevLng = this.stationForm.startLongitude;
        let prevLat = this.stationForm.startLatitude;
        let total = 0;
        for (let i = 0; i < list.length; i++) {
          const station = list[i];
          if (station.stationId && station.longitude && station.latitude) {
            // 有电站且有经纬度：计算该程里程，更新前一站坐标
            const dist = this.calcDistance(prevLng, prevLat, station.longitude, station.latitude);
            // 里程保留2位小数
            const mileage = Math.round(dist * 100) / 100;
            this.$set(station, 'segmentMileage', mileage);
            total += mileage;
            prevLng = station.longitude;
            prevLat = station.latitude;
          } else {
            // 无电站或无经纬度：该程里程为0，前一站坐标保持不变
            this.$set(station, 'segmentMileage', 0);
          }
        }
        this.stationForm.totalMileage = Math.round(total * 100) / 100;
      },
      /**
       * Haversine公式计算两个经纬度之间的距离（单位：km）
       */
      calcDistance(lng1, lat1, lng2, lat2) {
        if (!lng1 || !lat1 || !lng2 || !lat2) return 0;
        const R = 6371;
        const dLat = ((lat2 - lat1) * Math.PI) / 180;
        const dLng = ((lng2 - lng1) * Math.PI) / 180;
        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos((lat1 * Math.PI) / 180) *
            Math.cos((lat2 * Math.PI) / 180) *
            Math.sin(dLng / 2) *
            Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
      },
      onStartAddressBlur() {
        // 地址失焦时自动地图定位
        this.$nextTick(() => {
          this.clickStartAddress();
        });
      },
      clickStartAddress() {
        if (!this.preForm.startAddress) {
          this.$message.warning('请输入巡检行程起点地址');
          return;
        }
        this.$nextTick(() => {
          // 复用 StationMap 的 parseAddress，将完整地址作为 cAddress 传入
          this.$refs.startMap.parseAddress(this.preForm.startAddress, ' ', obj => {
            this.preForm.startLongitude = obj.lng;
            this.preForm.startLatitude = obj.lat;
          });
        });
      },
      onStartMapClick(lngLat) {
        // 点击地图时获取经纬度和地址
        this.preForm.startLongitude = lngLat.lng;
        this.preForm.startLatitude = lngLat.lat;
        // 回填地址
        if (this.$refs.startMap && this.$refs.startMap.address) {
          this.preForm.startAddress = this.$refs.startMap.address;
        }
      },
      clearStation(index) {
        this.$set(this.stationForm.planStationList, index, {
          stationId: null,
          stationName: '',
          sort: index + 1,
          segmentMileage: 0,
        });
        // 重新计算里程
        this.recalcPlanMileage();
      },
      handleGenerate() {
        this.$refs.preForm.validate(valid => {
          if (!valid) return;
          this.generating = true;
          const preDTO = { ...this.preForm };
          inspectionPlanApi
            .generateAiStations(preDTO)
            .then(res => {
              this.stationForm.id = null;
              this.stationForm.cityId = res.cityId;
              this.stationForm.cityName = res.cityName;
              this.stationForm.inspectionDate = res.inspectionDate;
              this.stationForm.stationCount = res.stationCount;
              this.stationForm.startAddress = res.startAddress;
              this.stationForm.startLongitude = res.startLongitude;
              this.stationForm.startLatitude = res.startLatitude;
              this.stationForm.aiStationList = res.aiStationList || [];
              this.stationForm.planStationList = res.planStationList || [];
              this.stationForm.aiTotalMileage = res.aiTotalMileage || 0;
              this.stationForm.totalMileage = res.totalMileage || 0;
              this.stationForm.adjustReason = '';
              this.showStationConfig = true;
              // 加载该城市下的电站列表，用于计划巡检电站下拉选择
              this.loadStationOptions(res.cityId);
            })
            .finally(() => {
              this.generating = false;
            });
        });
      },
      handleSave() {
        this.$refs.stationForm.validate(valid => {
          if (!valid) return;
          if (!this.stationForm.planStationList || this.stationForm.planStationList.length === 0) {
            this.$message.warning('计划巡检电站不能为空');
            return;
          }
          if (this.stationForm.planStationList.some(s => !s.stationId)) {
            this.$message.warning('请选择所有计划巡检电站');
            return;
          }
          if (this.needAdjustReason && !this.stationForm.adjustReason) {
            this.$message.warning('巡检电站手动调整原因必填');
            return;
          }
          this.saving = true;
          const saveDTO = { ...this.stationForm };
          inspectionPlanApi
            .save(saveDTO)
            .then(() => {
              this.$message.success('保存成功');
              this.$emit('save-success');
              this.drawerVisible = false;
            })
            .finally(() => {
              this.saving = false;
            });
        });
      },
      handleCancel() {
        YkcDialog({
          dialogType: 'feedback',
          showTitle: false,
          showFooter: true,
          desc: '取消后操作将不被保存，确认是否取消？',
          onCancel: done => {
            done();
          },
          onConfirm: done => {
            done();
            this.drawerVisible = false;
          },
        });
      },
      onDrawerClose() {
        this.resetForm();
      },
      onDrawerCancel() {
        this.handleCancel();
        // 不自动关闭，由 YkcDialog 的 onConfirm 控制
      },
    },
  };
</script>

<style lang="scss" scoped>
  .drawer-main-body {
    padding: 20px;

    .flex {
      display: flex;
      align-items: center;
    }

    .ykc-map {
      width: 100%;
      height: 300px;
    }

    .station-list-section {
      margin-bottom: 20px;

      .section-title {
        font-size: 14px;
        font-weight: 600;
        color: #333;
        margin-bottom: 12px;
      }

      .station-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 8px 0;

        .station-sort {
          min-width: 60px;
          color: #606266;
        }

        .station-name {
          flex: 1;
          color: #333;
        }

        .station-mileage {
          color: #909399;
          font-size: 13px;
        }

        .clear-btn {
          color: #f56c6c;
        }
      }
    }
  }
</style>
